import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleContext } from 'components/utils/module/ModuleContext';
import { ReactNode, useContext, useState } from 'react';
import { ConfirmModal } from '../../utils/modals/ConfirmModal';
import { Caption2, H4 } from '../../utils/typo';
import { HasEditPermissionContext, ResourceFormModuleTypes, ResourceTable } from '../ResourceTable';
import { FloatingActionButton } from '../util/FloatingActionButton';
import { SlidePanel } from '../../utils/panels/AvoSlidePanel';
import { TeamCustomizableKnowledgeBaseForm } from './TeamCustomizableKnowledgeBaseForm';
import {
  TEAM_CUSTOMIZABLE_KNOWLEDGE_BASE_QUERY_KEY,
  TeamCustomizableKnowledgeBase,
  teamCustomizableKnowledgeBaseApi,
} from '../../../api/resources/team-customizable-knowledge-base';
import { mentionAPI } from '../../../api/mention';
import { DefaultModuleSuggestions } from '../../../hooks/useSuggestions';
import { useTeam } from 'hooks/useTeam';
import { useTeamKnowledgeBases } from '../../../hooks/module/useTeamKnowledgeBases';

const labels = {
  title: 'Team-Customizable Knowledge Base',
  input: 'knowledge base',
  button: 'New',
  data: 'knowledge bases',
};

export const TeamCustomizableKnowledgeBaseTable = () => {
  const { module } = useContext(ModuleContext);
  const { data } = useTeamKnowledgeBases();

  const [open, setOpen] = useState<boolean>(false);
  const [resourceId, setResourceId] = useState<number | null>(null);
  const onOpen = (resourceId: number | null) => {
    setOpen(true);
    setResourceId(resourceId);
  };
  const onClose = () => {
    setOpen(false);
  };

  return (
    <>
      <ResourceTable
        moduleType={module?.type as ResourceFormModuleTypes}
        labels={labels}
        data={data} // need to impl
        renderCard={(data) => ResourceCard(data, onOpen)}
        onOpen={onOpen}
      />
      <SlidePanel open={open} onClose={() => setOpen(false)}>
        <TeamCustomizableKnowledgeBaseForm
          resource={data.find((kb) => kb.id === resourceId)}
          onCancel={onClose}
        />
      </SlidePanel>
    </>
  );
};

const ResourceCard = (
  resource: TeamCustomizableKnowledgeBase,
  onOpen: (resourceId: number | null) => void
): ReactNode => {
  const { team } = useTeam();
  const { module } = useContext(ModuleContext);
  const { data } = useQuery({
    queryKey: ['team', team?.id, 'resources'],
    queryFn: mentionAPI.getModulesSuggestions,
    select: (data) => data.knowledge_base,
    initialData: DefaultModuleSuggestions,
  });
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: teamCustomizableKnowledgeBaseApi.delete,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [module?.type, module?.id, TEAM_CUSTOMIZABLE_KNOWLEDGE_BASE_QUERY_KEY],
      }),
  });
  const [open, setOpen] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const hasEditPermission = useContext(HasEditPermissionContext);
  const onEdit = () => hasEditPermission && resource?.id && onOpen(resource?.id);
  const onDelete = () => {
    resource?.id && mutate(resource.id);
    setDeleteModal(false);
  };
  const toggleDelete = () => setDeleteModal((prev) => !prev);

  return (
    <>
      <div
        className='group flex select-none justify-between rounded-xl bg-white px-[20px] py-[18px] hover:!bg-primary-200'
        onDoubleClick={onEdit}
      >
        <div className='flex flex-col gap-2'>
          <div>
            <H4>{resource.title}</H4>
          </div>
          <div className='gap-1'>
            <Caption2 className='text-gray-900'>Default Knowledge Base</Caption2>
            <Caption2 className='text-gray-700'>
              {data.find((kb) => kb.id === resource.default_knowledge_base)?.title}
            </Caption2>
          </div>
          <div className='gap-1'>
            <Caption2 className='text-gray-900'>Description</Caption2>
            <Caption2 className='text-gray-700'>{resource.description}</Caption2>
          </div>
        </div>
        {hasEditPermission && (
          <FloatingActionButton
            open={open}
            onClose={() => setOpen(false)}
            onEdit={onEdit}
            onDelete={toggleDelete}
          />
        )}
      </div>
      <ConfirmModal
        preset='delete'
        open={deleteModal}
        content='Are you sure you want to delete this trigger?'
        toggleModal={toggleDelete}
        performAction={onDelete}
      />
    </>
  );
};
